import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RoutePath } from "@/routes";

const sections = [
  { id: "architecture", label: "Architecture" },
  { id: "design-system", label: "Design System" },
  { id: "layout", label: "Layout & Nav" },
  { id: "state", label: "State Management" },
  { id: "dashboard", label: "Dashboard" },
  { id: "products", label: "Products" },
  { id: "product-detail", label: "Product Detail" },
  { id: "brand-signals", label: "Brand Signals" },
  { id: "rank-tracker", label: "Rank Tracker" },
  { id: "spi", label: "SPI Scorecard" },
  { id: "report", label: "Report Preview" },
  { id: "shared", label: "Shared Components" },
  { id: "mock-data", label: "Mock Data Layer" },
  { id: "integration", label: "Integration Notes" },
];

const Table = ({ headers, rows }: { headers: string[]; rows: string[][] }) => (
  <div className="overflow-x-auto rounded-md border border-border my-4">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-muted/60">
          {headers.map((h) => (
            <th key={h} className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border whitespace-nowrap">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-muted/20"}>
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-2 text-muted-foreground border-b border-border/50">
                <code className="text-xs bg-muted/40 px-1 py-0.5 rounded">{cell}</code>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Code = ({ children }: { children: string }) => (
  <pre className="bg-[hsl(240,20%,12%)] text-[hsl(0,0%,85%)] rounded-md p-4 my-4 text-xs leading-relaxed overflow-x-auto font-mono">
    {children}
  </pre>
);

const SectionTitle = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <h2 id={id} className="text-xl font-bold text-foreground mt-10 mb-4 pb-2 border-b border-border scroll-mt-6">
    {children}
  </h2>
);

const Sub = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-base font-semibold text-foreground mt-6 mb-2">{children}</h3>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{children}</p>
);

const Badge = ({ children, variant = "default" }: { children: React.ReactNode; variant?: string }) => {
  const colors: Record<string, string> = {
    default: "bg-primary/10 text-primary",
    green: "bg-trend-positive/10 text-trend-positive",
    red: "bg-destructive/10 text-destructive",
    muted: "bg-muted text-muted-foreground",
  };
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${colors[variant] || colors.default}`}>
      {children}
    </span>
  );
};

const DeveloperHandoff = () => {
  const [activeSection, setActiveSection] = useState("architecture");

  const handleNavClick = (id: string) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Left nav */}
      <div className="w-56 shrink-0 border-r border-border bg-card p-4 flex flex-col">
        <div className="mb-6">
          <h1 className="text-sm font-bold text-foreground tracking-tight">Developer Handoff</h1>
          <p className="text-xs text-muted-foreground mt-1">Signal by Recom</p>
          <p className="text-xs text-muted-foreground">Feb 27, 2026</p>
        </div>
        <nav className="flex flex-col gap-0.5">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => handleNavClick(s.id)}
              className={`text-left text-xs px-2.5 py-1.5 rounded transition-colors ${
                activeSection === s.id
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              }`}
            >
              {s.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Main content */}
      <ScrollArea className="flex-1">
        <div className="max-w-4xl mx-auto px-8 py-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground">Signal by Recom — Developer Handoff</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Complete technical documentation for integrating this prototype into the production application. 
              All components, data structures, and patterns are documented below.
            </p>
            <div className="flex gap-2 mt-3">
              <Badge>React 18</Badge>
              <Badge>TypeScript</Badge>
              <Badge>Tailwind CSS</Badge>
              <Badge>shadcn/ui</Badge>
              <Badge>Recharts</Badge>
            </div>
          </div>

          {/* ─── Architecture ─── */}
          <SectionTitle id="architecture">1. Architecture Overview</SectionTitle>

          <Sub>Tech Stack</Sub>
          <Table
            headers={["Layer", "Technology"]}
            rows={[
              ["Framework", "React 18 + TypeScript"],
              ["Build Tool", "Vite"],
              ["Styling", "Tailwind CSS + CSS custom properties (HSL tokens)"],
              ["UI Components", "shadcn/ui (Radix primitives)"],
              ["Routing", "react-router-dom v6"],
              ["Charts", "Recharts"],
              ["State", "React Context (AppDataContext)"],
              ["Icons", "lucide-react"],
              ["Excel Export", "xlsx"],
            ]}
          />

          <Sub>File Structure</Sub>
          <Code>{`src/
├── assets/products/        # Product images (PNG) + index.ts barrel
├── assets/                 # SVG logos (signal-logo.svg, recom-r-logo.svg)
├── components/
│   ├── ab-tests/           # Brand Signals & A/B Tests module
│   ├── dashboard/          # Dashboard-specific components
│   ├── keyword-bank/       # Keyword bank (product-level tab)
│   ├── keyword-landscape-output/  # Landscape detail/output views
│   ├── keyword-landscapes/ # Landscapes list & management
│   ├── layout/             # AppLayout, AppSidebar, modals
│   ├── rank-tracker/       # Rank tracking components
│   ├── search-terms/       # Search terms management
│   ├── seo-insights/       # SEO insights module
│   ├── shared/             # Reusable modals & empty states
│   ├── spi/                # SPI Scorecard components
│   └── ui/                 # shadcn/ui primitives
├── contexts/               # AppDataContext (brands, hero ASINs)
├── data/                   # Mock data files
├── hooks/                  # Custom hooks (use-toast, use-mobile)
├── pages/                  # Route-level page components
└── lib/                    # Utilities (cn helper)`}</Code>

          <Sub>Routing Table</Sub>
          <Table
            headers={["Route", "Page Component", "Description"]}
            rows={[
              [RoutePath.Home, "Index → DashboardContent", "Main dashboard"],
              [RoutePath.Products, "Products", "Product grid hub"],
              [RoutePath.ProductDetail, "ProductDetail", "Tabbed product view"],
              [RoutePath.ABTests, "ABTests → ABTestsContent", "Brand Signals & A/B Tests"],
              [RoutePath.RankTracker, "RankTracker", "Keyword rank tracking"],
              [RoutePath.SPI, "SignalPerformanceIndex", "SPI Scorecard"],
              [RoutePath.KeywordLandscapeDetail, "KeywordLandscapeOutput", "Landscape detail view"],
              [RoutePath.ReportPreview, "ReportPreview", "Rank tracker report"],
              [RoutePath.ReportDetail, "ReportPreview", "Shared report view"],
              [RoutePath.Profile, "Profile", "User profile"],
              [RoutePath.Settings, "Settings", "App settings"],
              ["*", "NotFound", "404 fallback"],
            ]}
          />

          {/* ─── Design System ─── */}
          <SectionTitle id="design-system">2. Design System & Theming</SectionTitle>
          
          <P>
            All colors are defined as HSL values in CSS custom properties (<code className="text-xs bg-muted px-1 rounded">src/index.css</code>). 
            Components reference these via Tailwind utility classes — never hardcoded hex/rgb.
          </P>

          <Sub>Core Palette</Sub>
          <Table
            headers={["Token", "Light Mode (HSL)", "Usage"]}
            rows={[
              ["--background", "240 33% 95%", "Page background (light lavender-gray)"],
              ["--foreground", "240 13% 35%", "Primary text"],
              ["--card", "0 0% 100%", "Card surfaces (white)"],
              ["--primary", "234 99% 66%", "Brand blue (#5264FE)"],
              ["--secondary", "240 38% 94%", "Secondary background (#E9E9F4)"],
              ["--muted", "240 38% 94%", "Muted backgrounds"],
              ["--muted-foreground", "240 10% 50%", "Secondary text"],
              ["--destructive", "2 100% 65%", "Error/delete red (#FF534C)"],
              ["--border", "236 17% 82%", "Border color (#CBCCD9)"],
            ]}
          />

          <Sub>Sidebar Tokens</Sub>
          <Table
            headers={["Token", "Value", "Usage"]}
            rows={[
              ["--sidebar-background", "247 74% 37%", "Deep purple (#2A19A5)"],
              ["--sidebar-foreground", "0 0% 100%", "White text"],
              ["--sidebar-accent", "247 60% 45%", "Active/hover state"],
              ["--sidebar-border", "247 50% 32%", "Divider lines"],
            ]}
          />

          <P>The sidebar uses an inline gradient override:</P>
          <Code>{`background: linear-gradient(to bottom, #342799 0%, #4C32F2 33%, #A242A2 72%, #FF534C 100%)`}</Code>

          <Sub>Trend & Gradient Colors</Sub>
          <Table
            headers={["Token", "Value", "Usage"]}
            rows={[
              ["--trend-positive", "155 79% 39%", "Green for gains/improvements"],
              ["--trend-negative", "2 100% 65%", "Red for declines/losses"],
              ["--trend-neutral", "237 11% 63%", "Gray for no change"],
              ["--gradient-brand", "linear-gradient(135deg, #342799, #A242A2, #FF534C)", "Brand gradient (AI + general brand accents)"],
            ]}
          />

          <Sub>Typography & Radius</Sub>
          <P>Font: <code className="text-xs bg-muted px-1 rounded">"proxima-nova", sans-serif</code> — set globally. Border radius: <code className="text-xs bg-muted px-1 rounded">3px</code> (deliberately sharp).</P>

          {/* ─── Layout ─── */}
          <SectionTitle id="layout">3. Global Layout & Navigation</SectionTitle>

          <Sub>AppLayout</Sub>
          <P>No global header. The layout is sidebar + content only. A small <code className="text-xs bg-muted px-1 rounded">SidebarTrigger</code> hamburger icon sits at the top of the content area.</P>
          <Code>{`┌──────────┬──────────────────────────────────┐
│          │ [≡] SidebarTrigger               │
│  Sidebar │                                  │
│          │  <main>                           │
│          │    {page content}                 │
│          │  </main>                          │
│          │                                  │
└──────────┴──────────────────────────────────┘`}</Code>

          <Sub>AppSidebar — Three Sections</Sub>
          <P><strong>Header:</strong> Signal logo (centered, links to /), Brand Selector (searchable Popover + Command pattern with hover-reveal delete, translucent bg-white/20 button).</P>
          
          <Table
            headers={["Nav Item", "Route", "Icon"]}
            rows={[
              ["Dashboard", RoutePath.Home, "LayoutDashboard"],
              ["Products", RoutePath.Products, "Package"],
              ["Brand Signals", RoutePath.ABTests, "FlaskConical"],
              ["Rank Tracker", RoutePath.RankTracker, "BarChart3"],
              ["SPI Scorecard", RoutePath.SPI, "ScanSearch"],
            ]}
          />

          <P><strong>Footer:</strong> Notifications button (red dot indicator), User dropdown (Avatar, email, role, menu items: Profile, Settings, Help, Docs, Sign Out), Recom "R" logo.</P>

          <Sub>Empty/No-Data State</Sub>
          <P>
            All pages check <code className="text-xs bg-muted px-1 rounded">getHeroAsinsByBrand(selectedBrandId).length === 0</code> and render <code className="text-xs bg-muted px-1 rounded">EmptyBrandState</code> — 
            centered PackageOpen icon, contextual message, and "Add Hero ASIN" CTA.
          </P>

          {/* ─── State ─── */}
          <SectionTitle id="state">4. State Management</SectionTitle>
          <P>Single React context (<code className="text-xs bg-muted px-1 rounded">AppDataContext</code>) wrapping the entire app. All data is currently in-memory (useState) — this is the primary integration point.</P>

          <Sub>Data Model</Sub>
          <Code>{`interface Brand {
  id: string;    // slug from name
  name: string;
}

interface HeroAsin {
  id: string;      // "hero-{timestamp}-{index}"
  asin: string;    // Amazon ASIN
  name: string;    // Product title
  brand: string;   // Brand display name
  brandId: string; // Foreign key to Brand.id
  imageUrl?: string;
}`}</Code>

          <Sub>Context Functions</Sub>
          <Table
            headers={["Function", "Signature", "Description"]}
            rows={[
              ["addBrand", "(brand: {name}) => void", "Creates brand with auto-generated slug ID"],
              ["deleteBrand", "(brandId) => void", "Deletes brand + cascades to all its ASINs"],
              ["setSelectedBrandId", "(brandId) => void", "Switches active brand (global filter)"],
              ["addHeroAsins", "(asins: string[], brandId) => void", "Adds ASINs with placeholder names"],
              ["deleteHeroAsin", "(asinId) => void", "Removes single ASIN"],
              ["getHeroAsinsByBrand", "(brandId) => HeroAsin[]", "Filters ASINs by brand"],
            ]}
          />

          <P>Default data: 2 brands — "Supergoop!" (8 pre-loaded ASINs with images from src/assets/products/) and "New Brand" (empty, demonstrates empty state).</P>

          {/* ─── Dashboard ─── */}
          <SectionTitle id="dashboard">5.1 Dashboard</SectionTitle>
          <P>Route: <code className="text-xs bg-muted px-1 rounded">/</code> — File: <code className="text-xs bg-muted px-1 rounded">src/pages/Index.tsx → DashboardContent.tsx</code></P>
          <P>Two-column layout on XL screens: left column (main content stacked vertically), right column (300px sticky Activity Panel).</P>

          <Sub>Content Sections (top to bottom)</Sub>
          <Table
            headers={["Section", "Component", "Details"]}
            rows={[
              ["KPI Cards", "DashboardKPIs.tsx", "4-col grid: Est. Organic Revenue, Units Sold, Avg Keyword Rank, Organic Conversion Rate. Green/red trend pills."],
              ["Sales Correlation Chart", "DashboardCorrelationChart.tsx", "Recharts ComposedChart, 3 Y-axes (Rank, Units, BSR), Pearson correlation badge."],
              ["Biggest Gains & Declines", "Inline in DashboardContent", "2-col card: green gains (left), red declines (right), 3 items each with rank change."],
              ["Brand Signals Stats", "Inline in DashboardContent", "4 mini KPI cards (no icons, centered text): Active Signals, Tests, CVR Lift, Sales."],
              ["Products Overview", "Inline in DashboardContent", "3-col grid: Hero Products count, Landscapes count, Signal AI Recommendations."],
              ["Recent Landscapes", "Inline in DashboardContent", "3-col card grid with phrase, ASIN, date, keyword count badge."],
              ["Activity Panel", "DashboardActivityPanel.tsx", "Right sidebar: Action Items (priority badges), Upcoming Refreshes, Recent Activity."],
            ]}
          />

          {/* ─── Products ─── */}
          <SectionTitle id="products">5.2 Products Hub</SectionTitle>
          <P>Route: <code className="text-xs bg-muted px-1 rounded">/products</code> — Responsive card grid (1/2/3/4 cols). Each card: product image, title (2-line clamp), ASIN. Hover: border highlight + image zoom. Click → Product Detail.</P>

          {/* ─── Product Detail ─── */}
          <SectionTitle id="product-detail">5.3 Product Detail</SectionTitle>
          <P>Route: <code className="text-xs bg-muted px-1 rounded">/products/:productId</code> — Full-bleed card with fixed header + scrollable tab content.</P>
          <Code>{`┌─────────────────────────────────────────────────┐
│ ← Back to all products                          │
│ [Image] Product Title                 [Stats]   │
│         ASIN | Amazon US link         Hero/Comp  │
│─────────────────────────────────────────────────│
│ SEO Insights | Keywords | Landscapes | A/B | ST │
│─────────────────────────────────────────────────│
│  {Tab Content - scrollable}                     │
└─────────────────────────────────────────────────┘`}</Code>
          <Table
            headers={["Tab", "Component", "Description"]}
            rows={[
              ["SEO Insights (default)", "SeoInsightsContentV2", "Aggregated keyword insights"],
              ["Keywords", "KeywordBankContentV2", "Keyword bank management"],
              ["Landscapes", "KeywordLandscapesContentV2", "Landscape list + drill-down"],
              ["A/B Tests", "ABTestsContentV2", "Product-level A/B tests"],
              ["Search Terms", "SearchTermsContentV2", "Backend keyword management"],
            ]}
          />

          {/* ─── Brand Signals ─── */}
          <SectionTitle id="brand-signals">5.4 Brand Signals</SectionTitle>
          <P>Route: <code className="text-xs bg-muted px-1 rounded">/ab-tests</code> — Brand-level experimentation hub with segmented control toggle between "Brand Signals" and "A/B Tests" views.</P>
          
          <Sub>Stats Section</Sub>
          <P>4-column stat cards with icons: Active Signals, Tests (X of Y running), Total CVR Lift, Incremental Sales. "Total Incremental Value" = sum of absolute differences from completed tests.</P>

          <Sub>Brand Signals Tab</Sub>
          <P>3-column card grid. Cards show hypothesis, confidence badge, validation status. Source icons: Signal AI (sparkle + gradient) vs User (avatar). Search + status filters + "Add Signal" modal.</P>

          <Sub>A/B Tests Tab</Sub>
          <P>Search + 4 filter dropdowns (Product, Status, Result, Scope). Unified ABTestTable with all tests flattened. Supports: view, edit, delete, bulk delete, upload results.</P>

          {/* ─── Rank Tracker ─── */}
          <SectionTitle id="rank-tracker">5.5 Rank Tracker</SectionTitle>
          <P>Route: <code className="text-xs bg-muted px-1 rounded">/rank-tracker</code></P>
          <Table
            headers={["Section", "Component", "Details"]}
            rows={[
              ["Header", "Inline", '"Add Keyword" button + overflow menu (Export CSV, Share Report)'],
              ["Signal AI Rank Report", "SignalAIRankReport.tsx", "Monthly AI analysis with gains/declines, expandable details"],
              ["Brand Correlation Summary", "BrandCorrelationSummary.tsx", "Multi-axis chart (rank vs sales vs BSR) with ASIN multi-select"],
              ["ASIN Products Table", "AsinProductsTable.tsx", "Expandable per-product keyword tables with sparklines"],
            ]}
          />
          <P>Key features: Date range filter (7d–90d + custom), ASIN multi-select, per-keyword sparkline + sales correlation modal, share report.</P>

          {/* ─── SPI ─── */}
          <SectionTitle id="spi">5.6 SPI Scorecard</SectionTitle>
          <P>Route: <code className="text-xs bg-muted px-1 rounded">/spi</code> — Three-phase flow:</P>
          <Table
            headers={["Phase", "Component", "Description"]}
            rows={[
              ["1. Entry", "SPIEntryForm", "ASIN input form"],
              ["2. Loading", "SPILoadingState", "Progress bar with cycling COSMO insight messages"],
              ["3. Scorecard", "SPIScorecard", "Overall SPI score (0-100), 5 categories, 15 COSMO questions with scores/findings/evidence"],
            ]}
          />

          {/* ─── Report ─── */}
          <SectionTitle id="report">5.7 Report Preview</SectionTitle>
          <P>Route: <code className="text-xs bg-muted px-1 rounded">/report/preview</code> — Standalone (no sidebar). Executive summary, brand correlation chart, per-ASIN keyword tables, simulated PDF export.</P>

          {/* ─── Shared ─── */}
          <SectionTitle id="shared">6. Shared Components & Patterns</SectionTitle>
          <Sub>Modals</Sub>
          <Table
            headers={["Modal", "File", "Trigger"]}
            rows={[
              ["Add Brand", "AddBrandModal.tsx", "Sidebar brand selector"],
              ["Add Hero ASIN", "AddHeroAsinModal.tsx", 'Various "Add Hero ASIN" buttons'],
              ["Add Keywords", "AddKeywordsModal.tsx", "Rank tracker, keyword bank"],
              ["Add Landscape", "AddLandscapeModal.tsx", "Dashboard, landscapes tab"],
            ]}
          />
          <Sub>UI Customizations</Sub>
          <P><strong>Button:</strong> CVA variants, rounded-[var(--radius)]. <strong>Card:</strong> bg-card, border-border. <strong>Badge:</strong> Multiple variants. <strong>Table:</strong> .scroll-shadow-x for horizontal overflow. <strong>Toast:</strong> Dual setup — Radix-based + Sonner for simple notifications.</P>

          {/* ─── Mock Data ─── */}
          <SectionTitle id="mock-data">7. Mock Data Layer</SectionTitle>
          <P>All data is currently mocked. Each file exports typed arrays/functions. The TypeScript interfaces define your API response contracts.</P>
          <Table
            headers={["File", "Key Exports", "Used By"]}
            rows={[
              ["mockKeywordData.ts", "mockKeywordData, heroAsins", "Rank Tracker, Dashboard"],
              ["mockLandscapeData.ts", "mockHeroProducts (with instances)", "Dashboard, Landscapes"],
              ["mockLandscapeOutputData.ts", "Landscape detail data", "Landscape Output page"],
              ["types/ab-tests.ts, utils/ab-test-utils.ts", "A/B domain types + labels", "A/B UI, product.api"],
              ["mockBrandSignalsData.ts", "mockBrandSignals", "Brand Signals, Dashboard"],
              ["mockSeoInsightsData.ts", "getSeoInsightsData()", "SEO Insights, Product Detail"],
            ]}
          />

          {/* ─── Integration ─── */}
          <SectionTitle id="integration">8. Integration Notes</SectionTitle>

          <Sub>Replacing Mock Data</Sub>
          <div className="bg-primary/5 border border-primary/20 rounded-md p-4 my-4 text-sm text-foreground">
            <ol className="list-decimal pl-4 space-y-1.5">
              <li>Each mock file exports <strong>TypeScript interfaces</strong> — use these as your API response contracts.</li>
              <li>The <code className="text-xs bg-muted px-1 rounded">AppDataContext</code> is the primary place to wire up brand/ASIN CRUD operations.</li>
              <li>Dashboard aggregations (Brand Signals stats, landscape stats) currently compute inline — move to API endpoints.</li>
            </ol>
          </div>

          <Sub>Brand Selection</Sub>
          <P><code className="text-xs bg-muted px-1 rounded">selectedBrandId</code> in AppDataContext is the global filter. Changing brand in sidebar instantly updates all views. "New Brand" (empty) demonstrates the empty state pattern.</P>

          <Sub>Product Images</Sub>
          <P>Currently served from <code className="text-xs bg-muted px-1 rounded">src/assets/products/</code> as static imports via barrel file. Replace with CDN/S3 URLs in production.</P>

          <Sub>Key UX Patterns to Preserve</Sub>
          <div className="bg-muted/40 border border-border rounded-md p-4 my-4 text-sm space-y-2">
            <div className="flex gap-2 items-start"><Badge variant="muted">1</Badge> <span className="text-muted-foreground">No global header — sidebar-only navigation with collapsible trigger</span></div>
            <div className="flex gap-2 items-start"><Badge variant="muted">2</Badge> <span className="text-muted-foreground">EmptyBrandState on all pages when brand has no data</span></div>
            <div className="flex gap-2 items-start"><Badge variant="muted">3</Badge> <span className="text-muted-foreground">Product-centric navigation — Keywords, Landscapes, SEO, A/B Tests, Search Terms are tabs within Product Detail</span></div>
            <div className="flex gap-2 items-start"><Badge variant="muted">4</Badge> <span className="text-muted-foreground">Brand Signals is the brand-level aggregate for experimentation</span></div>
            <div className="flex gap-2 items-start"><Badge variant="muted">5</Badge> <span className="text-muted-foreground">Trend pills use bg-trend-positive/10 text-trend-positive pattern consistently</span></div>
          </div>

          <Sub>Fonts</Sub>
          <P>Requires <strong>Proxima Nova</strong> loaded externally (Adobe Fonts, CDN, or self-hosted). Not bundled.</P>

          <Sub>Key Dependencies</Sub>
          <Table
            headers={["Package", "Purpose"]}
            rows={[
              ["xlsx", "Landscape data Excel export"],
              ["recharts", "All charts (correlation, sparklines, reports)"],
              ["cmdk", "Command palette pattern (brand selector)"],
              ["vaul", "Drawer component"],
              ["react-day-picker", "Calendar/date picker"],
            ]}
          />

          <Sub>CSS Patterns</Sub>
          <div className="bg-muted/40 border border-border rounded-md p-4 my-4 text-sm space-y-1.5 text-muted-foreground">
            <p>• <code className="text-xs bg-muted px-1 rounded">.scroll-shadow-x</code> — Horizontal scroll shadow for wide tables</p>
            <p>• Sidebar gradient is <strong>inline</strong> on the Sidebar component, not a CSS class</p>
            <p>• Border radius intentionally small (3px) for a sharp, professional look</p>
            <p>• <code className="text-xs bg-muted px-1 rounded">tabular-nums</code> class used on numeric displays for aligned digits</p>
          </div>

          <div className="mt-12 pt-6 border-t border-border text-center text-xs text-muted-foreground">
            End of handoff documentation. For questions, reference the source files directly or the live preview.
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default DeveloperHandoff;
